/* eslint-disable @typescript-eslint/no-explicit-any */
// TODO: This file was automatically migrated to TypeScript by `dev typescript migrate`.
//  The eslint disable comment above should be removed and the resulting errors fixed.
import React from 'react';
import _ from 'underscore';

import {ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import {getObjectTypeForModelTypeConstant} from 'app-core/framework/CoreObjectIntrospectionUtils';
import {ChemicalFormatType, EMPTY_MOLFILE} from 'chemistry/ChemistryConstants';
import {stripSaltsAndNotify} from 'chemistry/molecules/MoleculeUtil';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import makeCancelable from 'lib/makeCancelable';
import notifications from 'notifications';
import CreateBioentityInputs from 'registry/create-bioentity/CreateBioentityInputs';
import {CreateBioentityArgs} from 'registry/create-bioentity/CreateBioentityUtil';
import * as CreateBioentityUtil from 'registry/create-bioentity/CreateBioentityUtil';
import {isStudyableObjectType} from 'studies/studyable/StudyableUtils';
import {isDNAOligoSchema} from 'tag-schema/util/TagSchemaUtil';
import {LabelStyle} from 'ui-library/inputs/InputConstants';
import * as LegacyModalContainer from 'ui-library/LegacyModalContainer';
import {ExpandableModal} from 'ui-library/modals/ExpandableModal';
import Modal, {ModalProps} from 'ui-library/modals/Modal';

const CREATE_BIOENTITY_MODAL_KEY = 'CreateBioentityModal';

interface Props {
  onRequestClose: (...args: Array<any>) => any;
  createBioentityArgs: CreateBioentityArgs;
}

type State = any;

class CreateBioentityModal extends React.Component<Props, State> {
  cancellableCreateBFIPromise: any;
  static close(): void {
    LegacyModalContainer.close(CREATE_BIOENTITY_MODAL_KEY);
  }

  static open(modal: any, props: any): void {
    if (props == null) {
      props = {};
    }
    // eslint-disable-next-line no-restricted-syntax
    props = _.extend({}, props, {
      onRequestClose() {
        LegacyModalContainer.close(CREATE_BIOENTITY_MODAL_KEY);
      },
    });
    LegacyModalContainer.open(CREATE_BIOENTITY_MODAL_KEY, React.createElement(modal, props));
  }

  override state: State = {
    isPosting: false,
    createBioentityArgs: this.props.createBioentityArgs,
  };

  _updateCreateBioentityArgs = (values: Partial<CreateBioentityArgs>): void => {
    this.setState((oldState: State) => ({
      ...oldState,
      createBioentityArgs: {...oldState.createBioentityArgs, ...values},
    }));
  };

  override componentWillUnmount(): void {
    if (this.cancellableCreateBFIPromise) {
      this.cancellableCreateBFIPromise.cancel();
    }
  }

  _handleOnRequestSubmit = (): void => {
    this.setState({isPosting: true});
    FolderDefaults.set(FolderDefaultsKey.INVENTORY, this.state.createBioentityArgs.folderId);

    const createFn = (): void => {
      switch (this.props.createBioentityArgs.tagSchema!.folderItemType) {
        case ModelTypeConstants.MOLECULE:
          CreateBioentityUtil.create(this.state.createBioentityArgs).then(() =>
            stripSaltsAndNotify(
              ChemicalFormatType.MOLFILE,
              this.state.createBioentityArgs.chemicalSketcherValue
            )
          );
          break;
        case ModelTypeConstants.MIXTURE:
        case ModelTypeConstants.BASIC_FOLDER_ITEM:
          CreateBioentityUtil.create(this.state.createBioentityArgs);
          break;
        default:
          throw new Error(
            `CreateBioentityModal tried to create ${this.props.createBioentityArgs.tagSchema!.folderItemType}`
          );
      }
    };
    // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
    this.cancellableCreateBFIPromise = makeCancelable(Promise.resolve(createFn()));
    this.cancellableCreateBFIPromise.promise
      .then(() => {
        this.props.onRequestClose();
      })
      .catch((reason: any) => {
        notifications.notifyError(reason);
        this.setState({isPosting: false});
      });
  };

  _getDisabled = (): boolean => {
    const hasName = this.state.createBioentityArgs.name.trim().length > 0;
    const isProjectSelected = this.state.createBioentityArgs.folderId;
    const isSmilesEmpty = this.state.createBioentityArgs.smiles === '';
    const isChemicalSketcherEmpty = this.state.createBioentityArgs.chemicalSketcherValue === EMPTY_MOLFILE;
    const isPosting = this.state.isPosting;

    return !hasName || !isProjectSelected || (!isSmilesEmpty && !isChemicalSketcherEmpty) || isPosting;
  };

  override render(): JSX.Element {
    const isMolecule =
      this.props.createBioentityArgs.tagSchema!.folderItemType === ModelTypeConstants.MOLECULE;
    const objectType = getObjectTypeForModelTypeConstant(
      this.props.createBioentityArgs.tagSchema!.folderItemType
    );
    const modalChildren = (
      <CreateBioentityInputs
        createBioentityArgs={this.state.createBioentityArgs}
        onChange={this._updateCreateBioentityArgs}
        labelStyle={LabelStyle.LABEL}
        isOligoSchema={isDNAOligoSchema(this.props.createBioentityArgs.tagSchema!)}
        shouldShowNameField={true}
        showStudiesField={objectType ? isStudyableObjectType(objectType) : false}
        // Modal only creates but does not register entities, and all unregistered entities require a
        // project folder
        isProjectFolderRequired={true}
      />
    );
    const modalProps: ModalProps = {
      title: `Create ${this.props.createBioentityArgs.tagSchema!.name}`,
      onRequestClose: this.props.onRequestClose,
      onRequestSubmit: this._handleOnRequestSubmit,
      buttons: [
        {
          text: this.state.isPosting ? 'Creating...' : 'Create',
          submit: true,
          disabled: this._getDisabled(),
        },
      ],
      children: modalChildren,
      analyticsModule: ANALYTICS_MODULE.REGISTRY,
      analyticsModalType: 'create_bioentity',
    };
    return isMolecule ? <ExpandableModal {...modalProps} startMaximized={true} /> : <Modal {...modalProps} />;
  }
}

export default CreateBioentityModal;
